import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const experiences = [
  {
    company: "Vosyn Technologies",
    role: "Front-End Developer",
    bullets: [
      "Built and refined interfaces for AI-driven products, translating complex workflows into clear, responsive layouts using React, TypeScript, and modern component patterns.",
      "Implemented a multilingual video player with subtitles and speed controls using React/Next.js, Dash.js, and Material UI, ensuring responsive design, browser compatibility, and use of modern frontend frameworks.",
      "Accelerated release cycles by 20% by collaborating with backend, QA, and GCP teams, mentoring frontend developers, and supporting refactoring and process improvement.",
      "Boosted product quality and functionality by contributing to core features with solid attention to web accessibility, design systems, and coding standards.",
      "Increased collaboration efficiency by 15% using Notion and GitHub for technical documentation and coordination across design and engineering teams.",
    ],
  },
  {
    company: "Reliance Digital Jio Pvt Ltd",
    role: "Software Developer",
    bullets: [
      "Worked as a software developer on developing the next generation integration platform for internal applications. Integrated data from diverse back-end services and databases using technologies such as Angular, Java, and software tools like Visual Studio Code and Spring Boot.",
      "Engineered a high-performing website to monitor the approval system using Angular and TypeScript. Developed administrative websites utilizing Liferay technology.",
      "Managed and monitored user counts for a government Android application through Kibana, Microsoft Word, and Excel.",
      "Configured servers for government platform Android applications and websites and formulated SQL queries for database maintenance. Updated old code bases to modern development standards, improving functionality.",
      "Contributed to a positive team environment through effective communication, problem-solving, and collaboration skills. Discussed issues with team members to provide resolution and apply best practices.",
    ],
  },
];

const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.14, delayChildren: 0.08 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

function Experience() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: window.innerWidth < 768 ? 0.12 : 0.15,
      }
    );
    const currentRef = ref.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <motion.div
      ref={ref}
      className="mx-auto mt-3 max-w-6xl px-1 sm:px-0"
      variants={containerVariants}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
    >
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10 lg:items-start">
        {experiences.map((job) => (
          <motion.article
            key={job.company}
            variants={cardVariants}
            className="w-full p-6 text-slate-200 font-light md:p-8
              rounded-2xl border border-violet-400/20 bg-white/[0.03] backdrop-blur-md
              shadow-[0_20px_50px_-18px_rgba(15,23,42,0.75)] shadow-indigo-950/40
              transition-all duration-500 ease-out hover:border-sky-400/25 hover:shadow-[0_24px_56px_-16px_rgba(56,189,248,0.12)]"
          >
            <header className="mb-5 text-center">
              <h3 className="text-xl sm:text-2xl font-semibold tracking-tight bg-gradient-to-r from-slate-100 to-sky-100 bg-clip-text text-transparent">
                {job.company}
              </h3>
              <p className="mt-2 text-sm font-medium uppercase tracking-[0.12em] text-sky-200/75">
                {job.role}
              </p>
            </header>
            <ul className="mb-1 list-none space-y-4 pl-0">
              {job.bullets.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span
                    className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-sky-400 to-violet-400 shadow-[0_0_10px_rgba(56,189,248,0.6)]"
                    aria-hidden
                  />
                  <p className="font-normal text-slate-200/95 leading-relaxed text-left">{item}</p>
                </li>
              ))}
            </ul>
          </motion.article>
        ))}
      </div>
    </motion.div>
  );
}

export default Experience;
